<template>
  <!--
    「요금·결제」의 중도 해지·환불 창(설계안 §21.10 · 약관 제20조) — 까닭 둘(라디오)을 고르면 서버가 셈한 값(돌려줄 금액 · 언제까지 쓰는지
    · 셈 근거)을 읽어 보이고, 사유(필수)를 받아 실행한다. 돈의 셈은 서버 한 곳이고 실행 때 서버가 다시 셈한다 — 이 창은 옮길 뿐이다.
    중도 해지는 구독료 줄만 정산한다(§26.9) — 견적에 서버가 판정한 남은 사업자 추가 줄이 있으면 「청구 내역에서 따로 [청구 취소]로」 한 줄을
    셈과 같이 보인다. 말·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <el-dialog
    :title="$t(text.terminateTitle)"
    :visible="visible"
    width="460px"
    append-to-body
    data-testid="subscription-terminate-dialog"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
    @open="reset">
    <p class="subscription-terminate__store">{{ storeLabel }}</p>
    <el-form label-position="top" @submit.native.prevent>
      <el-form-item :label="$t(text.terminateRule)" :error="problems.rule">
        <el-radio-group v-model="form.rule" data-testid="subscription-terminate-rule" @change="loadEstimate">
          <el-radio v-for="option in ruleOptions" :key="option.value" :label="option.value">{{
            $t(option.label)
          }}</el-radio>
        </el-radio-group>
      </el-form-item>
      <div v-if="view" class="subscription-terminate__estimate" data-testid="subscription-terminate-estimate">
        <p v-if="view.refund" class="subscription-terminate__refund" data-testid="subscription-terminate-refund">
          {{ view.refund }}
        </p>
        <p v-if="view.usable" data-testid="subscription-terminate-usable">{{ view.usable }}</p>
        <p v-if="view.basis" class="subscription-terminate__basis" data-testid="subscription-terminate-basis">
          {{ view.basis }}
        </p>
        <p v-if="view.note" data-testid="subscription-terminate-note">{{ $t(view.note) }}</p>
        <p v-if="view.pending" data-testid="subscription-terminate-pending">{{ view.pending }}</p>
        <p v-if="view.addon" class="subscription-terminate__addon" data-testid="subscription-terminate-addon">
          {{ view.addon }}
        </p>
      </div>
      <el-form-item :label="$t(text.stopReason)" :error="problems.reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="3"
          data-testid="subscription-terminate-reason"
          :placeholder="$t(text.reasonPlaceholder)"></el-input>
      </el-form-item>
    </el-form>
    <p v-if="problem" class="subscription-terminate__problem" data-testid="subscription-terminate-problem">
      {{ problem }}
    </p>
    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t(text.close) }}</el-button>
      <el-button
        v-if="view"
        size="small"
        type="danger"
        data-testid="subscription-terminate-submit"
        :loading="busy"
        @click="submit"
        >{{ $t(view.submit) }}</el-button
      >
    </span>
  </el-dialog>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import { latestGuard } from '@/utils/latestGuard'
import {
  SUBSCRIPTION_TEXT,
  SUBSCRIPTION_WRITE_CONFIG,
  TERMINATION_RULE_OPTIONS,
  terminateOutcomeOf,
  terminateRequestOf,
  terminationQueryOf,
  terminationViewOf,
} from '@/utils/subscriptionAdmin'

const noProblems = () => ({ rule: '', reason: '' })

export default {
  name: 'SubscriptionTerminateDialog',

  mixins: [mainVueMixin],

  props: {
    visible: { type: Boolean, default: false },
    subscriptionId: { type: Number, default: null },
    /** 매장 이름(「QA 피자 · S2600…」) */
    storeLabel: { type: String, default: '' },
  },

  data: function () {
    return {
      text: SUBSCRIPTION_TEXT,
      ruleOptions: TERMINATION_RULE_OPTIONS,
      form: { rule: '', reason: '' },
      /** 까닭별 서버 셈(`GET …/termination`) — 까닭을 바꾼 순간 앞 까닭의 셈이 보이지 않게 까닭으로 나눠 둔다 */
      estimates: {},
      problems: noProblems(),
      /** 칸이 아닌 실패(서버 문장·결제 취소가 안 된 사유) */
      problem: '',
      busy: false,
      /**
       * 늦게 온 응답을 버리는 가드(`utils/latestGuard`) — 축마다 하나: 셈 읽기 · 실행. 창을 다시 열면([reset]) 둘 다 무효로 한다 —
       * 앞 창의 셈이 새 창의 셈을 덮거나, 앞 창의 실행 결과가 새 창을 닫거나 새 창에 말하지 않게
       */
      estimateGuard: latestGuard(),
      submitGuard: latestGuard(),
    }
  },

  computed: {
    view: function () {
      return terminationViewOf(this.estimates[this.form.rule])
    },
  },

  methods: {
    /** 열 때마다 새로 — 고른 까닭·셈·사유를 비우고, 앞 창에서 나가 있던 응답을 버린다 */
    reset: function () {
      this.estimateGuard.invalidate()
      this.submitGuard.invalidate()
      this.form = { rule: '', reason: '' }
      this.estimates = {}
      this.problems = noProblems()
      this.problem = ''
      this.busy = false
    },

    /** 까닭을 골랐다 — 그 까닭의 셈을 서버에서 읽는다(실패는 인터셉터가 알린다). 그 사이 새로 읽었거나 창을 다시 열었으면 버린다 */
    loadEstimate: function (rule) {
      const query = terminationQueryOf(this.subscriptionId, rule)
      const token = this.estimateGuard.begin()
      this.problems.rule = ''

      this.mainVue.$axios.search(
        query.url,
        query.params,
        function (result) {
          if (!this.estimateGuard.isLatest(token)) return
          this.estimates = Object.assign({}, this.estimates, { [rule]: result })
        }.bind(this)
      )
    },

    /** 그 까닭의 셈을 버리고 다시 읽는다 — 셈이 오기 전까지 실행 단추는 없다(낡은 금액으로 실행하지 않게) */
    reloadEstimate: function (rule) {
      const rest = Object.assign({}, this.estimates)
      delete rest[rule]
      this.estimates = rest
      this.loadEstimate(rule)
    },

    /**
     * 실행 — 보내고, 결과를 한 줄로 말하고, 부모가 상세·목록을 다시 읽게 한다. 결제 취소가 안 된 갈래(200 값 — 해지하지 않았다)는 이
     * 창 안에서 그 사유를 말한다(청구 취소와 같은 규칙). 그 밖의 거절(400 사유 빔·200자 넘음 · 404 · 409 · 503 정기결제 상점 설정 없음)은
     * 서버 문장을 창 안에서 한 번 말한다.
     *
     * 응답이 왔을 때 창을 다시 열었으면(앞 창의 실행) 이 창의 상태는 건드리지 않는다 — 다만 서버가 처리했거나 그 사이 상태가 바뀐
     * 것(200·409)은 부모가 상세·목록을 다시 읽게 한다.
     */
    submit: async function () {
      const request = terminateRequestOf(this.subscriptionId, this.form)
      this.problems = noProblems()
      this.problem = ''
      if (!request.ok) {
        this.problems = Object.assign(noProblems(), { [request.field]: this.$t(request.problem) })
        return
      }

      const token = this.submitGuard.begin()
      this.busy = true
      try {
        const response = await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)
        const outcome = terminateOutcomeOf(response && response.data)

        this.$emit('changed')
        if (!this.submitGuard.isLatest(token)) return
        if (outcome.done) {
          this.mainVue.$message({ type: 'success', message: outcome.message })
          this.$emit('update:visible', false)
        } else {
          this.problem = outcome.message
        }
      } catch (error) {
        // 409 — `CHARGE_NOT_CANCELABLE`(실행 중에 그 줄이 먼저 취소됨 — 청구 취소와 겹침) · `CHARGE_PENDING`(결과 모르는 결제를 서버가
        // 아직 맞추지 못함). 그 문장을 말하고, 그 까닭의 셈을 다시 읽고, 부모가 다시 읽는다(자동 결제 멈추기·청구 취소와 같은 규칙).
        // 창은 열어 두고 적은 사유는 남긴다 — 실행은 다시 보내지 않는다
        const changed = !!error && error.status === 409
        if (this.submitGuard.isLatest(token)) {
          this.problem = httpErrorMessage(error, this.$t(this.text.writeFailed))
          if (changed) this.reloadEstimate(request.body.rule)
        }
        if (changed) this.$emit('changed')
      } finally {
        if (this.submitGuard.isLatest(token)) this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-terminate__store {
  margin: 0 0 8px;
  color: var(--color-text-regular);
}

.subscription-terminate__estimate {
  margin: 0 0 12px;
  color: var(--color-text-primary);
  word-break: keep-all;
}

.subscription-terminate__estimate p {
  margin: 0 0 4px;
}

.subscription-terminate__refund {
  font-weight: 600;
}

.subscription-terminate__addon {
  color: var(--color-warning);
}

.subscription-terminate__basis {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.subscription-terminate__problem {
  margin: 0;
  color: var(--color-danger);
}
</style>
